import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Plus, RefreshCw, Search, Users } from "lucide-react";
import Header from "../components/dashboard/Header";
import api, { formatApiErrorDetail } from "../lib/api";
import { CompanyDrawer, CopyLink } from "../components/onboarding/CompanyDrawer";
import { NewOnboardingDialog } from "../components/onboarding/NewOnboardingDialog";

const sel = "bg-brand-surface border border-brand-border rounded-md px-3 py-2 text-sm text-brand-ink focus:outline-none focus:border-brand-primary";

function CompanyRow({ c, onOpen }) {
  return (
    <tr onClick={() => onOpen(c.id)} data-testid={`onb-row-${c.id}`} className="border-b border-brand-border last:border-0 hover:bg-brand-hover cursor-pointer transition-colors">
      <td className="px-5 py-3">
        <p className="text-brand-ink font-medium">{c.company_name}</p>
        <p className="text-xs text-brand-muted">{c.contact_name} {c.contact_email && `· ${c.contact_email}`}</p>
      </td>
      <td className="px-3 py-3 text-brand-muted">{c.location}</td>
      <td className="px-3 py-3 text-brand-muted">{c.start_date || "—"}</td>
      <td className="px-3 py-3">
        <span className="inline-flex items-center gap-1 text-brand-ink" data-testid={`onb-row-count-${c.id}`}><Users className="h-3.5 w-3.5 text-brand-tertiary" /> {c.member_count}</span>
        {c.pending_count > 0 && <span className="ml-2 text-[10px] uppercase tracking-[0.1em] text-amber-800 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5">{c.pending_count} to review</span>}
      </td>
      <td className="px-3 py-3 text-xs text-brand-tertiary">{c.source === "yardi" ? "Yardi" : "Manual"}{c.status === "closed" && " · closed"}</td>
      <td className="px-5 py-3 text-right"><CopyLink token={c.token} testid={`onb-copy-${c.id}`} /></td>
    </tr>
  );
}

export default function Onboarding() {
  const [params, setParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [locations, setLocations] = useState([]);
  const [filters, setFilters] = useState({ location: "", status: "open", q: "" });
  const [creating, setCreating] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const openId = params.get("company");
  const setOpen = (id) => setParams(id ? { company: id } : {});

  const load = useCallback(() => api.get("/onboarding", { params: Object.fromEntries(Object.entries(filters).filter(([, v]) => v)) })
    .then(({ data }) => setData(data)).catch(() => toast.error("Could not load onboarding")), [filters]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/bookings/config").then(({ data }) => setLocations(data.mappings.map((m) => m.location))).catch(() => {}); }, []);

  const sync = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post("/onboarding/sync");
      if (data.skipped) toast.message("A Yardi check is already running. Results will appear in a minute.");
      else if (data.error) toast.error("Yardi sync failed", { description: data.error });
      else toast.success(`${data.created || 0} new member compan${data.created === 1 ? "y" : "ies"} from Yardi`);
    }
    catch (e) { toast.error("Yardi sync failed", { description: formatApiErrorDetail(e.response?.data?.detail) }); }
    finally { setSyncing(false); load(); }
  };
  const set = (k) => (e) => setFilters((f) => ({ ...f, [k]: e.target.value }));
  const companies = data?.companies || [];

  return (
    <div className="min-h-screen bg-brand-page relative z-10">
      <Header />
      <main className="max-w-[1400px] mx-auto px-6 lg:px-12 py-10 space-y-8">
        <div className="flex flex-wrap items-end gap-4 justify-between">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-brand-tertiary">Locations &amp; Members</p>
            <h1 className="font-serif text-4xl lg:text-5xl font-light tracking-tight text-brand-ink mt-2" data-testid="onb-title">New Member Onboarding</h1>
            <p className="text-sm text-brand-muted mt-1">Each new company signed in Yardi gets its own link. Copy it into the Yardi welcome email, and the team adds their details and ID.</p>
          </div>
          <div className="flex gap-2">
            <button onClick={sync} disabled={syncing} data-testid="onb-sync-btn" className="inline-flex items-center gap-1.5 border border-brand-border rounded-full px-4 py-2 text-sm text-brand-ink hover:bg-brand-hover transition-colors disabled:opacity-50">
              <RefreshCw className={`h-3.5 w-3.5 ${syncing ? "animate-spin" : ""}`} /> {syncing ? "Checking Yardi…" : "Check Yardi now"}
            </button>
            <button onClick={() => setCreating(true)} data-testid="onb-new-btn" className="inline-flex items-center gap-1.5 bg-brand-ink text-brand-page rounded-full px-4 py-2 text-sm hover:opacity-90 transition-opacity"><Plus className="h-3.5 w-3.5" /> New link</button>
          </div>
        </div>
        {data?.sync?.last_run && <p className="text-xs text-brand-tertiary -mt-4" data-testid="onb-sync-info">Yardi checked {new Date(data.sync.last_run).toLocaleString()} · checks hourly{data.sync.last_error && ` · error: ${data.sync.last_error}`}</p>}
        <div className="flex flex-wrap gap-3">
          <select value={filters.location} onChange={set("location")} data-testid="onb-filter-location" className={sel}>
            <option value="">All locations</option>{locations.map((l) => <option key={l}>{l}</option>)}
          </select>
          <select value={filters.status} onChange={set("status")} data-testid="onb-filter-status" className={sel}>
            <option value="open">Open links</option><option value="closed">Closed</option><option value="">All</option>
          </select>
          <div className="relative flex-1 min-w-[220px] max-w-sm">
            <Search className="h-3.5 w-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-brand-tertiary" />
            <input value={filters.q} onChange={set("q")} placeholder="Search company" data-testid="onb-search" className={`${sel} w-full pl-8`} />
          </div>
        </div>
        {companies.length ? (
          <div className="border border-brand-border rounded-xl overflow-x-auto bg-brand-surface" data-testid="onb-table">
            <table className="w-full text-sm min-w-[820px]">
              <thead><tr className="text-left text-[10px] uppercase tracking-[0.15em] text-brand-tertiary border-b border-brand-border">
                <th className="px-5 py-3 font-normal">Company</th><th className="px-3 py-3 font-normal">Location</th><th className="px-3 py-3 font-normal">Start</th>
                <th className="px-3 py-3 font-normal">Team</th><th className="px-3 py-3 font-normal">Source</th><th className="px-5 py-3" />
              </tr></thead>
              <tbody>{companies.map((c) => <CompanyRow key={c.id} c={c} onOpen={setOpen} />)}</tbody>
            </table>
          </div>
        ) : <div className="border border-dashed border-brand-border rounded-xl py-16 text-center text-sm text-brand-muted" data-testid="onb-empty">{data ? "No onboarding links match these filters." : "Loading…"}</div>}
      </main>
      <CompanyDrawer id={openId} onClose={() => setOpen(null)} onChanged={load} />
      <NewOnboardingDialog open={creating} onOpenChange={setCreating} locations={locations} onCreated={(c) => { setCreating(false); load(); setOpen(c.id); }} />
    </div>
  );
}
